<script setup lang="ts">
  import { marked } from 'marked'
  import { FARE_TABLE_CLUB_LEVEL } from '@/components/fare-table/types'
  import type { TFareTableClubLevel } from '@/components/fare-table/types'
  import type { AssetStoryblok, ServiceBodyStoryblok } from '@/storyblok/component-types-sb'
  import { toAssetImageProps } from '@/utils/storyblok/normalizers'

  const props = withDefaults(
    defineProps<{
      blok: ServiceBodyStoryblok
      logo?: string | AssetStoryblok | null
      expanded: boolean
      clubLevel?: TFareTableClubLevel
    }>(),
    {
      logo: null,
      expanded: false,
      clubLevel: FARE_TABLE_CLUB_LEVEL.none
    }
  )

  function getMarkedText(key: keyof ServiceBodyStoryblok, condition = true) {
    const value = props.blok[key]
    if (props.blok.full || !value || !condition) return ''
    return marked(value)
  }

  const additionalText = computed(() => getMarkedText('additionalText'))
  const expandedText = computed(() => getMarkedText('expandedText', props.expanded && !props.blok.full))
  const btClubText = computed(() => getMarkedText('btClubText'))
  const btClubVipText = computed(() => getMarkedText('btClubVipText'))
  const btClubExecutiveText = computed(() => getMarkedText('btClubExecutiveText'))
  const selectedClubLevel = computed<TFareTableClubLevel>(() => props.clubLevel || FARE_TABLE_CLUB_LEVEL.none)
  const selectedClubText = computed(() => {
    if (selectedClubLevel.value === FARE_TABLE_CLUB_LEVEL.vip) {
      return btClubVipText.value || btClubExecutiveText.value || btClubText.value
    }
    if (selectedClubLevel.value === FARE_TABLE_CLUB_LEVEL.executive) {
      return btClubExecutiveText.value || btClubText.value
    }
    if (selectedClubLevel.value === FARE_TABLE_CLUB_LEVEL.club) {
      return btClubText.value
    }
    return ''
  })
  const showClubInfo = computed(
    () => selectedClubLevel.value !== FARE_TABLE_CLUB_LEVEL.none && !!selectedClubText.value
  )

  const logoImageProps = computed(() => {
    if (!props.logo) return { src: '', alt: 'Club logo' }
    if (typeof props.logo === 'string') return { src: props.logo, alt: 'Club logo' }
    const imageProps = toAssetImageProps(props.logo)
    return {
      src: imageProps.src || '',
      alt: imageProps.alt || 'Club logo'
    }
  })

  const textClass = 'text-secondary-light text-body-3'
</script>

<template>
  <div class="text-subtitle-3 md:text-subtitle-2">{{ blok.mainText }}</div>
  <div
    v-if="additionalText"
    class="[&_a]:font-inherit whitespace-normal break-words [&_a]:text-inherit [&_a]:underline [&_a]:underline-offset-2"
    :class="textClass"
    v-html="additionalText"
  />

  <div
    v-if="expandedText"
    class="[&_a]:font-inherit whitespace-normal break-words [&_a]:text-inherit [&_a]:underline [&_a]:underline-offset-2"
    :class="textClass"
    v-html="expandedText"
  />

  <div
    v-if="showClubInfo"
    class="flex flex-col gap-3 border-t border-dashed border-t-bt-green pt-4"
  >
    <NuxtImg
      v-if="logoImageProps.src"
      :src="logoImageProps.src"
      :alt="logoImageProps.alt"
      width="63"
      height="18"
    />

    <div
      v-if="selectedClubText"
      class="[&_a]:font-inherit whitespace-normal break-words [&_a]:text-inherit [&_a]:underline [&_a]:underline-offset-2"
      :class="textClass"
      v-html="selectedClubText"
    />
  </div>
</template>
